import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Loader2, Copy, Check, Wand2, AlertTriangle, CheckCircle, XCircle, RefreshCw, Save } from 'lucide-react';
import { dbImages, type GalleryImage, type ImageSEOData } from '../utils/dbBackend';
import { API_ENDPOINTS, isBackendAvailable, authToken } from '../utils/api';

interface SEOResult {
  filename_base: string;
  filenames: {
    [key: string]: { jpg: string; webp: string };
  };
  primary_alt_he: string;
  primary_alt_en: string;
  title_he: string;
  figcaption_he: string;
  suggested_page: string;
  keywords_he: string[];
  keywords_en: string[];
  breed_detected: string;
  breed_confidence: number;
  other_attributes: string[];
  json_ld_imageobject: string;
  html_figure: string;
  srcset_snippet: string;
  suggested_sitemap_update: boolean;
  privacy_note: string | null;
  confidence_overall: number;
}

const SERVICE_OPTIONS = [
  { value: '', label: 'Auto-detect' },
  { value: 'grooming', label: 'תספורת מלאה (Grooming)' },
  { value: 'bath', label: 'אמבטיה (Bath)' },
  { value: 'nails', label: 'גזירת ציפורניים (Nails)' },
  { value: 'cat_grooming', label: 'טיפוח חתולים (Cat Grooming)' },
  { value: 'puppy_intro', label: 'היכרות לגורים (Puppy Intro)' },
  { value: 'hypoallergenic', label: 'טיפול היפואלרגני (Hypoallergenic)' },
  { value: 'mobile', label: 'שירות נייד (Mobile)' },
];

// OpenAI calls go through backend proxy at /.netlify/functions/openai

export function ImageSEOAssistant() {
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [selectedImage, setSelectedImage] = useState<GalleryImage | null>(null);
  const [description, setDescription] = useState('');
  const [serviceType, setServiceType] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analyzingAll, setAnalyzingAll] = useState(false);
  const [currentResult, setCurrentResult] = useState<SEOResult | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [expandedSEO, setExpandedSEO] = useState<string | null>(null);

  useEffect(() => {
    loadImages();
  }, []);

  const loadImages = async () => {
    try {
      // Check if backend is available
      const backendAvailable = await isBackendAvailable();

      if (backendAvailable) {
        // Fetch from Netlify Functions backend
        const response = await fetch(API_ENDPOINTS.images);
        const data = await response.json();

        if (data.images && data.images.length > 0) {
          // Convert backend format to GalleryImage format for SEO assistant
          const backendImages: GalleryImage[] = data.images.map((img: any) => ({
            id: img.id,
            imageUrl: img.imageUrl || img.afterUrl || img.beforeUrl,
            uploadedAt: img.uploadedAt,
            seo: img.seo,
          }));
          setImages(backendImages);
          return;
        }
      }
      
      // Fallback to IndexedDB
      const allImages = await dbImages.getAll();
      setImages(allImages);
    } catch (error: any) {
      setMessage({ type: 'error', text: `Failed to load images: ${error.message}` });
    }
  };

  const getImageDataUrl = async (image: GalleryImage): Promise<string | null> => {
    // If we have a blob, convert it to data URL
    if (image.imageBlob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(image.imageBlob!);
      });
    }

    // If we have a URL (from backend), fetch it and convert to base64
    // OpenAI Vision API cannot access relative URLs
    if (image.imageUrl) {
      try {
        const response = await fetch(image.imageUrl);
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status}`);
        }
        const imageBlob = await response.blob();
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            resolve(reader.result as string);
          };
          reader.onerror = reject;
          reader.readAsDataURL(imageBlob);
        });
      } catch (error) {
        return null;
      }
    }

    return null;
  };

  const analyzeImage = async (image: GalleryImage, desc?: string, service?: string): Promise<SEOResult | null> => {
    try {
      const today = new Date().toISOString().slice(0, 10);

      // Get the image for analysis
      const dataUrl = await getImageDataUrl(image);
      if (!dataUrl) {
        throw new Error('No image data available');
      }

      const imageContent = {
        type: 'image_url',
        image_url: {
          url: dataUrl,
          detail: 'high'
        }
      };

      const systemPrompt = `You are an expert pet grooming image analyst and SEO specialist for ShowGroom, a pet grooming salon in Zikhron-Yakov (זכרון־יעקב), Israel.

Analyze the provided image and generate comprehensive SEO metadata. You must:
1. Identify the pet breed (dog/cat), colors, coat type, and any visible conditions (matted, clean, trimmed)
2. Detect if it's a "before" or "after" grooming image
3. Note any props (collars, bows, bandanas)
4. Check if any people are visible (flag for privacy)
5. Determine the scene (indoor salon, outdoor, etc.)

IMPORTANT: All Hebrew text must be natural, fluent Hebrew. Include "זכרון־יעקב" in relevant places.
Brand name: ShowGroom
Location: Zikhron-Yakov (זכרון־יעקב)
Phone: 077-2200052
Booking URL: https://www.showgroom.co.il/book

Return ONLY valid JSON (no markdown, no code blocks) with this exact structure:
{
  "filename_base": "showgroom_{before/after}_{breed}_{colors}_zikhron_yakov_{yyyy-mm}",
  "filenames": {
    "400": {"jpg": "filename_400.jpg", "webp": "filename_400.webp"},
    "800": {"jpg": "filename_800.jpg", "webp": "filename_800.webp"},
    "1200": {"jpg": "filename_1200.jpg", "webp": "filename_1200.webp"}
  },
  "primary_alt_he": "Hebrew alt text 8-15 words describing image, breed, service, location",
  "primary_alt_en": "English alt text",
  "title_he": "Short Hebrew title with ShowGroom",
  "figcaption_he": "Hebrew caption with CTA - include booking URL or phone",
  "suggested_page": "gallery|services/grooming|services/bath|home",
  "keywords_he": ["up to 8 Hebrew keywords"],
  "keywords_en": ["English keywords"],
  "breed_detected": "breed name or unknown",
  "breed_confidence": 0.0-1.0,
  "other_attributes": ["color", "coat-type", "accessories", "scene"],
  "privacy_note": null or "Contains person(s) — publish only with explicit consent.",
  "confidence_overall": 0.0-1.0
}`;

      const userPrompt = `Analyze this pet grooming image.
${desc ? `User description: ${desc}` : ''}
${service ? `Service type: ${service}` : ''}
Upload date: ${today}

Generate complete SEO metadata in the exact JSON format specified.`;

      // Use backend proxy if available, otherwise call OpenAI directly won't work (no key)
      const backendAvailable = await isBackendAvailable();
      const token = authToken.get();

      const requestBody = {
        model: 'gpt-4o',
        messages: [
          { role: 'system', content: systemPrompt },
          {
            role: 'user',
            content: [
              { type: 'text', text: userPrompt },
              imageContent
            ]
          }
        ],
        max_tokens: 2000,
        temperature: 0.3
      };

      const response = await fetch(
        backendAvailable ? API_ENDPOINTS.openai : API_ENDPOINTS.openai,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
          body: JSON.stringify(requestBody),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || errorData.error || `API Error: ${response.status}`);
      }

      const data = await response.json();
      const content = data.choices[0]?.message?.content;

      if (!content) {
        throw new Error('No response from AI');
      }

      // Parse the JSON response
      const cleanContent = content.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      const parsedResult = JSON.parse(cleanContent);

      // Generate the additional HTML/JSON-LD snippets
      const filename = parsedResult.filename_base;
      
      const jsonLd = {
        "@context": "https://schema.org",
        "@type": "ImageObject",
        "name": parsedResult.title_he,
        "description": parsedResult.primary_alt_he,
        "contentUrl": `https://www.showgroom.co.il/images/${filename}_1200.jpg`,
        "thumbnailUrl": `https://www.showgroom.co.il/images/${filename}_400.jpg`,
        "author": {
          "@type": "Organization",
          "name": "ShowGroom",
          "url": "https://www.showgroom.co.il"
        },
        "copyrightHolder": {
          "@type": "Organization",
          "name": "ShowGroom"
        },
        "uploadDate": today
      };

      const htmlFigure = `<figure class="showgroom-gallery-image" itemscope itemtype="https://schema.org/ImageObject">
  <picture>
    <source type="image/webp" srcset="
      /images/${filename}_400.webp 400w,
      /images/${filename}_800.webp 800w,
      /images/${filename}_1200.webp 1200w"
      sizes="(max-width: 480px) 400px, (max-width: 960px) 800px, 1200px">
    <img 
      src="/images/${filename}_800.jpg"
      srcset="
        /images/${filename}_400.jpg 400w,
        /images/${filename}_800.jpg 800w,
        /images/${filename}_1200.jpg 1200w"
      sizes="(max-width: 480px) 400px, (max-width: 960px) 800px, 1200px"
      alt="${parsedResult.primary_alt_he}"
      title="${parsedResult.title_he}"
      loading="lazy"
      decoding="async"
      itemprop="contentUrl"
      width="1200"
      height="800">
  </picture>
  <figcaption itemprop="caption">${parsedResult.figcaption_he}</figcaption>
</figure>`;

      const srcsetSnippet = `<img 
  src="/images/${filename}_800.jpg"
  srcset="
    /images/${filename}_400.jpg 400w,
    /images/${filename}_800.jpg 800w,
    /images/${filename}_1200.jpg 1200w"
  sizes="(max-width: 480px) 400px, (max-width: 960px) 800px, 1200px"
  alt="${parsedResult.primary_alt_he}"
  title="${parsedResult.title_he}"
  loading="lazy"
  decoding="async">`;

      return {
        ...parsedResult,
        json_ld_imageobject: `<script type="application/ld+json">\n${JSON.stringify(jsonLd, null, 2)}\n</script>`,
        html_figure: htmlFigure,
        srcset_snippet: srcsetSnippet,
        suggested_sitemap_update: true
      };

    } catch (err: any) {
      throw err;
    }
  };

  const handleAnalyzeSelected = async () => {
    if (!selectedImage) return;

    setIsAnalyzing(true);
    setMessage(null);
    setCurrentResult(null);

    try {
      const result = await analyzeImage(
        selectedImage,
        description || selectedImage.description,
        serviceType || selectedImage.serviceType
      );

      if (result) {
        setCurrentResult(result);

        // Save SEO data to database
        const seoData: ImageSEOData = {
          filename_base: result.filename_base,
          primary_alt_he: result.primary_alt_he,
          primary_alt_en: result.primary_alt_en,
          title_he: result.title_he,
          figcaption_he: result.figcaption_he,
          suggested_page: result.suggested_page,
          keywords_he: result.keywords_he,
          keywords_en: result.keywords_en,
          breed_detected: result.breed_detected,
          breed_confidence: result.breed_confidence,
          other_attributes: result.other_attributes,
          html_figure: result.html_figure,
          srcset_snippet: result.srcset_snippet,
          json_ld_imageobject: result.json_ld_imageobject,
          confidence_overall: result.confidence_overall,
          privacy_note: result.privacy_note,
        };

        // Save SEO data - use backend API if available, otherwise IndexedDB
        const backendAvailable = await isBackendAvailable();
        if (backendAvailable) {
          const token = authToken.get();
          const response = await fetch(`${API_ENDPOINTS.images}/${selectedImage.id}/seo`, {
            method: 'PATCH',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(seoData),
          });
          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.error || 'Failed to save SEO data');
          }
        } else {
          await dbImages.updateSEO(selectedImage.id, seoData);
        }
        await loadImages();
        setMessage({ type: 'success', text: 'SEO data generated and saved!' });
      }
    } catch (err: any) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleAnalyzeAll = async () => {
    const imagesWithoutSEO = images.filter(img => !img.seo?.analyzedAt);
    if (imagesWithoutSEO.length === 0) {
      setMessage({ type: 'success', text: 'All images already have SEO data!' });
      return;
    }

    setAnalyzingAll(true);
    setMessage(null);

    let successCount = 0;
    let errorCount = 0;

    for (const image of imagesWithoutSEO) {
      try {
        setMessage({ type: 'success', text: `Analyzing ${successCount + errorCount + 1}/${imagesWithoutSEO.length}...` });
        
        const result = await analyzeImage(image, image.description, image.serviceType);
        
        if (result) {
          const seoData: ImageSEOData = {
            filename_base: result.filename_base,
            primary_alt_he: result.primary_alt_he,
            primary_alt_en: result.primary_alt_en,
            title_he: result.title_he,
            figcaption_he: result.figcaption_he,
            suggested_page: result.suggested_page,
            keywords_he: result.keywords_he,
            keywords_en: result.keywords_en,
            breed_detected: result.breed_detected,
            breed_confidence: result.breed_confidence,
            other_attributes: result.other_attributes,
            html_figure: result.html_figure,
            srcset_snippet: result.srcset_snippet,
            json_ld_imageobject: result.json_ld_imageobject,
            confidence_overall: result.confidence_overall,
            privacy_note: result.privacy_note,
          };

          // Save SEO data to backend
          const backendAvailable = await isBackendAvailable();
          if (backendAvailable) {
            const token = authToken.get();
            const response = await fetch(`${API_ENDPOINTS.images}/${image.id}/seo`, {
              method: 'PATCH',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
              },
              body: JSON.stringify(seoData),
            });
            if (!response.ok) {
              throw new Error('Failed to save SEO data');
            }
          } else {
            await dbImages.updateSEO(image.id, seoData);
          }
          successCount++;
        }
      } catch (err: any) {
        errorCount++;
      }

      // Small delay to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 1000));
    }

    await loadImages();
    setAnalyzingAll(false);
    setMessage({ 
      type: successCount > 0 ? 'success' : 'error', 
      text: `Completed! ${successCount} analyzed, ${errorCount} errors.` 
    });
  };

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      // Failed to copy to clipboard
    }
  };

  const CopyButton = ({ text, field }: { text: string; field: string }) => (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => copyToClipboard(text, field)}
      className="h-8 px-2"
    >
      {copiedField === field ? (
        <Check className="h-4 w-4 text-green-500" />
      ) : (
        <Copy className="h-4 w-4" />
      )}
    </Button>
  );

  const imagesWithSEO = images.filter(img => img.seo?.analyzedAt);
  const imagesWithoutSEO = images.filter(img => !img.seo?.analyzedAt);

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="py-4 text-center">
            <div className="text-2xl font-bold" style={{ color: '#444444' }}>{images.length}</div>
            <div className="text-sm" style={{ color: '#444444' }}>Total Images</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4 text-center">
            <div className="text-2xl font-bold text-green-600">{imagesWithSEO.length}</div>
            <div className="text-sm" style={{ color: '#444444' }}>With SEO</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4 text-center">
            <div className="text-2xl font-bold text-orange-500">{imagesWithoutSEO.length}</div>
            <div className="text-sm" style={{ color: '#444444' }}>Need SEO</div>
          </CardContent>
        </Card>
      </div>

      {/* Bulk Actions */}
      {imagesWithoutSEO.length > 0 && (
        <Card>
          <CardContent className="py-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium" style={{ color: '#444444' }}>
                  {imagesWithoutSEO.length} images need SEO analysis
                </p>
                <p className="text-sm" style={{ color: '#444444' }}>
                  Generate SEO data for all images automatically
                </p>
              </div>
              <Button
                onClick={handleAnalyzeAll}
                disabled={analyzingAll}
                className="flex items-center gap-2"
                style={{ backgroundColor: '#F29B70', color: '#444444' }}
              >
                {analyzingAll ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Wand2 className="h-4 w-4" />
                    Analyze All ({imagesWithoutSEO.length})
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Message */}
      {message && (
        <div
          className={`p-4 rounded-lg ${
            message.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
          }`}
        >
          {message.text}
        </div>
      )}

      {/* Image Gallery */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Gallery Images</span>
            <Button variant="ghost" size="sm" onClick={loadImages}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {images.length === 0 ? (
            <p className="text-center py-8" style={{ color: '#444444' }}>
              No images in gallery. Upload images in the Gallery Images tab first.
            </p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((image) => {
                const imgUrl = dbImages.getImageUrl(image);
                const hasSEO = !!image.seo?.analyzedAt;
                const isSelected = selectedImage?.id === image.id;

                return (
                  <div
                    key={image.id}
                    className={`relative rounded-lg overflow-hidden cursor-pointer border-2 transition-all ${
                      isSelected ? 'border-orange-500 ring-2 ring-orange-200' : 'border-transparent hover:border-gray-300'
                    }`}
                    onClick={() => {
                      setSelectedImage(image);
                      setDescription(image.description || '');
                      setServiceType(image.serviceType || '');
                      setCurrentResult(null);
                    }}
                    style={{ backgroundColor: 'rgba(255, 255, 255, 0.5)' }}
                  >
                    {imgUrl ? (
                      <img
                        src={imgUrl}
                        alt="Gallery"
                        className="w-full aspect-square object-cover"
                      />
                    ) : (
                      <div className="w-full aspect-square bg-gray-200 flex items-center justify-center">
                        <span className="text-gray-400 text-xs">No image</span>
                      </div>
                    )}
                    <div className={`absolute top-2 right-2 p-1 rounded-full ${
                      hasSEO ? 'bg-green-500' : 'bg-orange-500'
                    }`}>
                      {hasSEO ? (
                        <CheckCircle className="h-4 w-4 text-white" />
                      ) : (
                        <XCircle className="h-4 w-4 text-white" />
                      )}
                    </div>
                    {image.seo?.breed_detected && (
                      <div className="p-2 text-center text-sm font-medium" style={{ color: '#444444' }}>
                        {image.seo.breed_detected}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Selected Image Detail */}
      {selectedImage && (
        <Card>
          <CardHeader>
            <CardTitle>Analyze Selected Image</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Preview */}
              <div>
                <Label>Image</Label>
                <div className="mt-2 border rounded-lg overflow-hidden">
                  {dbImages.getImageUrl(selectedImage) ? (
                    <img
                      src={dbImages.getImageUrl(selectedImage)!}
                      alt="Selected"
                      className="w-full aspect-square object-cover"
                    />
                  ) : (
                    <div className="aspect-square bg-gray-100 flex items-center justify-center">
                      <span className="text-gray-400 text-sm">No image</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Options */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Service Type (Optional)</Label>
                  <Select value={serviceType} onValueChange={setServiceType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Auto-detect" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_OPTIONS.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Description (Optional)</Label>
                  <Textarea
                    placeholder="Add details about breed, service, special notes..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    rows={3}
                  />
                </div>

                <Button
                  onClick={handleAnalyzeSelected}
                  disabled={isAnalyzing}
                  className="w-full"
                  style={{ backgroundColor: '#F29B70', color: '#444444' }}
                >
                  {isAnalyzing ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <Wand2 className="mr-2 h-4 w-4" />
                      {selectedImage.seo?.analyzedAt ? 'Re-analyze Image' : 'Generate SEO Data'}
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Show existing SEO data */}
            {selectedImage.seo?.analyzedAt && !currentResult && (
              <div className="mt-4 p-4 rounded-lg bg-green-50 border border-green-200">
                <div className="flex items-center gap-2 mb-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-green-800">SEO Data Available</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium">Breed:</span> {selectedImage.seo.breed_detected} ({Math.round((selectedImage.seo.breed_confidence || 0) * 100)}%)
                  </div>
                  <div dir="rtl">
                    <span className="font-medium">Alt (HE):</span> {selectedImage.seo.primary_alt_he}
                  </div>
                  <div>
                    <span className="font-medium">Keywords:</span> {selectedImage.seo.keywords_he?.join(', ')}
                  </div>
                  <div>
                    <span className="font-medium">Analyzed:</span> {new Date(selectedImage.seo.analyzedAt).toLocaleString()}
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Current Result */}
      {currentResult && (
        <div className="space-y-4">
          {/* Privacy Warning */}
          {currentResult.privacy_note && (
            <Card className="border-yellow-300 bg-yellow-50">
              <CardContent className="py-4">
                <div className="flex items-center gap-2 text-yellow-800">
                  <AlertTriangle className="h-5 w-5" />
                  <span className="font-medium">{currentResult.privacy_note}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Confidence */}
          <Card>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <span className="font-medium">Confidence:</span>
                <span className={`font-bold ${currentResult.confidence_overall >= 0.7 ? 'text-green-600' : currentResult.confidence_overall >= 0.5 ? 'text-yellow-600' : 'text-red-600'}`}>
                  {(currentResult.confidence_overall * 100).toFixed(0)}%
                </span>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span>Breed:</span>
                <span className="font-medium">{currentResult.breed_detected} ({(currentResult.breed_confidence * 100).toFixed(0)}%)</span>
              </div>
            </CardContent>
          </Card>

          {/* Alt Text Hebrew */}
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center justify-between">
                Alt Text (Hebrew)
                <CopyButton text={currentResult.primary_alt_he} field="alt_he" />
              </CardTitle>
            </CardHeader>
            <CardContent className="py-2" dir="rtl">
              <p className="text-sm bg-gray-100 p-2 rounded">{currentResult.primary_alt_he}</p>
            </CardContent>
          </Card>

          {/* Keywords */}
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center justify-between">
                Keywords (Hebrew)
                <CopyButton text={currentResult.keywords_he.join(', ')} field="keywords" />
              </CardTitle>
            </CardHeader>
            <CardContent className="py-2">
              <div className="flex flex-wrap gap-2" dir="rtl">
                {currentResult.keywords_he.map((kw, i) => (
                  <span key={i} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm">
                    {kw}
                  </span>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Figcaption */}
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center justify-between">
                Caption (Hebrew)
                <CopyButton text={currentResult.figcaption_he} field="caption" />
              </CardTitle>
            </CardHeader>
            <CardContent className="py-2" dir="rtl">
              <p className="text-sm bg-gray-100 p-2 rounded">{currentResult.figcaption_he}</p>
            </CardContent>
          </Card>

          {/* Code Snippets */}
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center justify-between">
                HTML Figure
                <CopyButton text={currentResult.html_figure} field="html" />
              </CardTitle>
            </CardHeader>
            <CardContent className="py-2">
              <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded overflow-x-auto max-h-48">
                {currentResult.html_figure}
              </pre>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-sm flex items-center justify-between">
                JSON-LD
                <CopyButton text={currentResult.json_ld_imageobject} field="jsonld" />
              </CardTitle>
            </CardHeader>
            <CardContent className="py-2">
              <pre className="text-xs bg-gray-900 text-gray-100 p-3 rounded overflow-x-auto max-h-48">
                {currentResult.json_ld_imageobject}
              </pre>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Images with SEO - Expandable List */}
      {imagesWithSEO.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Images with SEO Data ({imagesWithSEO.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {imagesWithSEO.map((image) => (
                <div
                  key={image.id}
                  className="border rounded-lg overflow-hidden"
                >
                  <div
                    className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50"
                    onClick={() => setExpandedSEO(expandedSEO === image.id ? null : image.id)}
                  >
                    {dbImages.getImageUrl(image) && (
                      <img
                        src={dbImages.getImageUrl(image)!}
                        alt=""
                        className="w-12 h-12 object-cover rounded"
                      />
                    )}
                    <div className="flex-1">
                      <div className="font-medium text-sm">{image.seo?.breed_detected || 'Unknown breed'}</div>
                      <div className="text-xs text-gray-500" dir="rtl">{image.seo?.primary_alt_he?.substring(0, 50)}...</div>
                    </div>
                    <div className="text-xs text-gray-400">
                      {image.seo?.analyzedAt && new Date(image.seo.analyzedAt).toLocaleDateString()}
                    </div>
                  </div>
                  
                  {expandedSEO === image.id && image.seo && (
                    <div className="border-t p-3 bg-gray-50 space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="font-medium">Keywords:</span>
                        <CopyButton text={image.seo.keywords_he?.join(', ') || ''} field={`kw-${image.id}`} />
                      </div>
                      <div className="flex flex-wrap gap-1" dir="rtl">
                        {image.seo.keywords_he?.map((kw, i) => (
                          <span key={i} className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">
                            {kw}
                          </span>
                        ))}
                      </div>
                      
                      <div className="flex justify-between mt-2">
                        <span className="font-medium">Alt Text:</span>
                        <CopyButton text={image.seo.primary_alt_he || ''} field={`alt-${image.id}`} />
                      </div>
                      <p className="text-xs bg-white p-2 rounded" dir="rtl">{image.seo.primary_alt_he}</p>
                      
                      <div className="flex gap-2 mt-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(image.seo?.html_figure || '', `html-${image.id}`)}
                        >
                          Copy HTML
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(image.seo?.json_ld_imageobject || '', `ld-${image.id}`)}
                        >
                          Copy JSON-LD
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}